import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingGreenPointPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'Green Point Professional Painters - Apartment & Residential Painting | Commercial Specialists',
    description: 'Expert painting services in Green Point, Sea Point, and Waterfront. Specialized in apartment, residential, and commercial painting. Over 16 years serving the Atlantic Seaboard with quality finishes.',
    keywords: 'painters Green Point, apartment painting Green Point, house painters Green Point, residential painting, commercial painting, Atlantic Seaboard painters',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters Green Point',
          description: 'Professional apartment and residential painting',
          address: 'Green Point, Cape Town',
          priceRange: '$$',
          serviceArea: ['Green Point', 'Sea Point', 'Waterfront', 'De Waterkant', 'Mouille Point']
        }),
        generateServiceSchema({
          name: 'Apartment & Residential Painting',
          description: 'Complete painting solutions for apartments and homes',
          provider: 'Painters Green Point',
          areaServed: ['Green Point', 'Sea Point', 'Waterfront'],
          serviceType: ['Apartment Painting', 'Residential Painting', 'Commercial Painting']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const listing = {
    name: "Green Point Pro Painters",
    location: "Green Point, Cape Town",
    website: "https://paintersgreenpoint.co.za",
    heroImage: "https://public.readdy.ai/ai/img_res/61c3bd11f69d76f5040b38e7b5f1d737.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=modern%20apartment%20interior%20freshly%20painted%20living%20room%20with%20large%20windows%20city%20views%20contemporary%20furniture%20clean%20white%20walls%20professional%20finish&width=800&height=600&seq=greenpoint-gallery1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=residential%20building%20exterior%20painting%20professional%20painters%20on%20scaffolding%20working%20on%20facade%20white%20walls%20modern%20architecture%20Green%20Point&width=800&height=600&seq=greenpoint-gallery2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=commercial%20office%20space%20interior%20painting%20professional%20team%20applying%20paint%20to%20walls%20modern%20workspace%20clean%20environment%20business%20property&width=800&height=600&seq=greenpoint-gallery3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=apartment%20complex%20common%20area%20freshly%20painted%20hallway%20modern%20lighting%20clean%20walls%20professional%20finish%20residential%20building&width=800&height=600&seq=greenpoint-gallery4&orientation=landscape"
    ],
    services: [
      {
        title: "Residential Painting",
        description: "Comprehensive painting services for apartments, townhouses, and residential properties in Green Point. We specialize in both interior and exterior painting, delivering exceptional results that enhance your living space.",
        features: ["Interior apartment painting", "Exterior facade painting", "Balcony and patio painting", "Color consultation services"]
      },
      {
        title: "Apartment Painting",
        description: "Specialized painting solutions for apartment buildings and complexes. We work efficiently with body corporates and property managers to coordinate projects with minimal disruption to residents.",
        features: ["Individual unit painting", "Common area painting", "Stairwell and hallway painting", "Body corporate coordination"]
      },
      {
        title: "Commercial Painting",
        description: "Professional painting services for offices, retail spaces, and commercial properties. We understand business needs and work around your schedule to minimize operational disruption.",
        features: ["Office space painting", "Retail store painting", "After-hours service available", "Quick project turnaround"]
      }
    ],
    serviceAreas: ["Green Point", "Mouille Point", "Three Anchor Bay", "Sea Point", "Waterfront", "De Waterkant"],
    about: "Green Point Pro Painters is a premier painting contractor serving Green Point and the surrounding Atlantic Seaboard areas. With over 13 years of experience in residential and commercial painting, we have established ourselves as one of the most trusted and reliable painting companies in Cape Town's vibrant Green Point neighborhood.",
    aboutExtended: [
      "Our expertise spans across all types of painting projects, from individual apartment units to large-scale commercial buildings. We understand the unique character of Green Point properties, from modern high-rise apartments to charming Victorian homes, and we tailor our services to meet the specific needs of each property type.",
      "What distinguishes us is our commitment to quality workmanship and customer satisfaction. We use only premium quality paints and materials from leading brands, ensuring long-lasting results that withstand Cape Town's coastal climate. Our team of skilled painters is fully trained, certified, and insured, giving you complete peace of mind throughout your project.",
      "We work extensively with body corporates, property management companies, and individual homeowners, providing comprehensive painting solutions that enhance property value and aesthetic appeal. Our project managers ensure clear communication, efficient scheduling, and meticulous attention to detail from initial consultation through to final inspection."
    ],
    ourApproach: [
      "Detailed consultation and color planning",
      "Comprehensive surface preparation and repairs",
      "Premium quality paints and materials",
      "Professional application techniques",
      "Minimal disruption to residents and businesses",
      "Thorough cleanup and final inspection"
    ],
    whyChoose: [
      "13+ years of industry experience",
      "Residential and commercial specialists",
      "Premium quality materials",
      "Body corporate approved",
      "Fully licensed and insured",
      "Satisfaction guaranteed"
    ],
    testimonials: [
      {
        name: "Mark Thompson",
        rating: 5,
        text: "Excellent service from start to finish. They painted our apartment beautifully and were very professional throughout. The attention to detail was impressive."
      },
      {
        name: "Lisa Petersen",
        rating: 5,
        text: "As a body corporate trustee, I highly recommend Green Point Pro Painters. They completed our building's exterior painting on schedule and the results are outstanding."
      },
      {
        name: "James Wilson",
        rating: 5,
        text: "They painted our office after hours so we didn't lose any business time. Professional, efficient, and the quality is excellent. Very happy with the service."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Green Point Pro Painters</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Professional Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Green Point Pro Painters</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-bold mb-6">Areas We Serve</h3>
                  <p className="text-gray-700 text-lg mb-4">We proudly serve the following areas in Cape Town's Atlantic Seaboard:</p>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Professional Painting Services</h2>
                <p className="text-gray-700 text-lg mb-8">We offer comprehensive painting services for residential and commercial properties in Green Point.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">Green Point, Mouille Point, Sea Point</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 7:30AM-5:30PM</p>
                    <p className="text-gray-600">Sat: 8AM-12PM</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Green Point</li>
                <li>Sea Point</li>
                <li>Waterfront</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
